import VacantLandForm from "../forms/formTypes/vacantLandForm";
import { get } from "./validationHelper";
import {
  validateRequired,
  validateEgyptianPhoneNumber,
} from "../../../helpers/Validation";

export const userSchema = [
  {
    key: "applicant_role_id",
    path: "applicant_role_id",
    type: "select",
    name: "صفة مقدم الإقرار",
  },
  {
    key: "applicant_role_other",
    path: "applicant_role_other",
    type: "select",
    name: "صفة مقدم الإقرار الأخرى",
    when: (data) => parseInt(data?.applicant_role_id) === 7,
  },
  {
    key: "joint_ownership_document",
    path: "joint_ownership_document.full_url",
    type: "file",
    name: "صورة عقد الشيوع",
    when: (data) => parseInt(data?.applicant_role_id) === 2,
  },
  {
    key: "power_of_attorney",
    path: "power_of_attorney.full_url",
    type: "file",
    name: "صورة التوكيل",
    when: (data) => [5, 6, 7].includes(parseInt(data?.applicant_role_id)),
  },
];

// Common for applicant_role_id in [5,6,7]
const taxpayerCommonSchemaForRoles567 = [
  {
    key: "type_id",
    path: "taxpayer.type_id",
    type: "select",
    name: "نوع المكلف بأداء الضريبة",
    when: (data) => [5, 6, 7].includes(parseInt(data?.applicant_role_id)),
  },
  // Individual (type_id === 1)
  {
    key: "first_name",
    path: "taxpayer.first_name",
    type: "input",
    name: "الإسم الأول للمكلف بأداة الضريبة",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 1,
  },
  {
    key: "last_name",
    path: "taxpayer.last_name",
    type: "input",
    name: "باقي الإسم للمكلف بأداء الضريبة",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 1,
  },
  {
    key: "nationality_id",
    path: "taxpayer.nationality_id",
    type: "select",
    name: "الجنسية",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 1,
  },
  // Egyptian
  {
    key: "national_id",
    path: "taxpayer.national_id",
    type: "input",
    name: "الرقم القومي",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 1 &&
      parseInt(data?.taxpayer?.nationality_id) === 63,
    customValidate: (v, data) => {
      if (!v || String(v).trim().length !== 14) {
        return {
          valid: false,
          message: "يجب إدخال الرقم القومي المكون من 14 رقمًا فقط",
        };
      }
      // Allow hooking your external validator:
      if (typeof data?.__validateNationalID === "function") {
        const ok = data.__validateNationalID(v);
        return ok
          ? true
          : {
              valid: false,
              message: "يجب أن يتبع صيغة الرقم القومي المصري الصحيحة.",
            };
      }
      return true;
    },
  },
  {
    key: "national_id_attachment",
    path: "taxpayer.national_id_attachment.full_url",
    type: "customFile",
    name: "مرفق الرقم القومي",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 1 &&
      parseInt(data?.taxpayer?.nationality_id) === 63,
  },
  // Foreigner
  {
    key: "passport_number",
    path: "taxpayer.passport_number",
    type: "input",
    name: "رقم جواز السفر",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 1 &&
      parseInt(data?.taxpayer?.nationality_id) !== 63,
  },
  {
    key: "passport_attachment",
    path: "taxpayer.passport_attachment.full_url",
    type: "customFile",
    name: "مرفق جواز السفر",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 1 &&
      parseInt(data?.taxpayer?.nationality_id) !== 63,
  },

  // Corporate (type_id === 2)
  {
    key: "name",
    path: "taxpayer.name",
    type: "input",
    name: "اسم المكلف بأداء الضريبة",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 2,
  },
  {
    key: "commercial_register",
    path: "taxpayer.commercial_register",
    type: "input",
    name: "رقم السجل التجاري",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 2,
  },
  {
    key: "commercial_register_attachment",
    path: "taxpayer.commercial_register_attachment.full_url",
    type: "customFile",
    name: "صورة السجل التجاري",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 2,
  },
  {
    key: "tax_card_number",
    path: "taxpayer.tax_card_number",
    type: "input",
    name: "رقم البطاقة الضريبية",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 2,
  },
  {
    key: "tax_card_attachment",
    path: "taxpayer.tax_card_attachment.full_url",
    type: "customFile",
    name: "صورة البطاقة الضريبية",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 2,
  },
  {
    key: "other_attachment_name",
    path: "taxpayer.other_attachment_name",
    type: "input",
    name: "اسم المرفق الآخر",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 2 &&
      !!get(data, "taxpayer.other_attachment.full_url"),
  },
  {
    key: "other_attachment",
    path: "taxpayer.other_attachment.full_url",
    type: "customFile",
    name: "المرفق",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 2 &&
      !!get(data, "taxpayer.other_attachment_name"),
  },

  // Common contact for roles 5,6,7
  // {
  //   key: "phone",
  //   path: "taxpayer.phone",
  //   type: "input",
  //   name: "رقم الهاتف المحمول",
  //   when: (data) =>
  //     [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
  //     parseInt(data?.taxpayer?.type_id) === 1,
  // },
  {
    key: "phone",
    path: "taxpayer.phone",
    type: "input",
    name: "رقم الهاتف المحمول",
    when: (data) =>
      [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
      parseInt(data?.taxpayer?.type_id) === 1,
    customValidate: (v, data) => {
      const countryCode = data?.taxpayer?.country_code || "eg";
      const dialCode = data?.taxpayer?.dial_code;
      const nationalNumber = v.replace(dialCode, "").replace(/\D/g, "");

      // Egypt-specific rules
      if (countryCode === "eg") {
        const regex = /^(?:0?)(10|11|12|15)\d{8}$/;
        if (!regex.test(nationalNumber)) {
          return {
            valid: false,
            message:
              "رقم الهاتف المدخل يجب أن يكون رقم هاتف محمول مصريًا صالحًا. يُرجى اختيار مفتاح الدولة (+20) وإدخال رقم هاتف مصري صحيح",
          };
        }
      }

      // General validation for other countries
      if (!/^\+?[0-9]{6,15}$/.test("+" + v)) {
        return {
          valid: false,
          message:
            "يجب ادخال الصيغة الصحيحة لرقم الهاتف المحمول وفقا للدولة المختارة.",
        };
      }

      // Length rule (≤15 digits excluding country code)
      if (nationalNumber.length < 6 || nationalNumber.length > 15) {
        return {
          valid: false,
          message:
            "يجب ادخال الصيغة الصحيحة لرقم الهاتف المحمول وفقا للدولة المختارة",
        };
      }

      return true;
    },
  },
  // {
  //   key: "email",
  //   path: "taxpayer.email",
  //   type: "input",
  //   name: "البريد الإلكترونى",
  //   when: (data) => [5, 6, 7].includes(parseInt(data?.applicant_role_id)),
  // },
];

// For applicant_role_id === 2 branch (single owner)
const taxpayerSchemaForRole2 = [
  {
    key: "name",
    path: "taxpayer.name",
    type: "input",
    name: "اسم المكلف بأداء الضريبة",
    when: (data) => parseInt(data?.applicant_role_id) === 2,
  },
  {
    key: "nationality_id",
    path: "taxpayer.nationality_id",
    type: "select",
    name: "الجنسية",
    when: (data) => parseInt(data?.applicant_role_id) === 2,
  },
  // Egyptian
  {
    key: "national_id",
    path: "taxpayer.national_id",
    type: "input",
    name: "الرقم القومي",
    when: (data) =>
      parseInt(data?.applicant_role_id) === 2 &&
      parseInt(data?.taxpayer?.nationality_id) === 63,
    customValidate: (v, data) => {
      if (!v || String(v).trim().length !== 14) {
        return {
          valid: false,
          message: "يجب إدخال الرقم القومي المكون من 14 رقمًا فقط",
        };
      }
      if (typeof data?.__validateNationalID === "function") {
        const ok = data.__validateNationalID(v);
        return ok
          ? true
          : {
              valid: false,
              message: "يجب أن يتبع صيغة الرقم القومي المصري الصحيحة.",
            };
      }
      return true;
    },
  },
  {
    key: "national_id_attachment",
    path: "taxpayer.national_id_attachment.full_url",
    type: "customFile",
    name: "مرفق الرقم القومي",
    when: (data) =>
      parseInt(data?.applicant_role_id) === 2 &&
      parseInt(data?.taxpayer?.nationality_id) === 63,
  },
  // Foreigner
  {
    key: "passport_number",
    path: "taxpayer.passport_number",
    type: "input",
    name: "رقم جواز السفر",
    when: (data) =>
      parseInt(data?.applicant_role_id) === 2 &&
      parseInt(data?.taxpayer?.nationality_id) !== 63,
  },
  {
    key: "passport_attachment",
    path: "taxpayer.passport_attachment.full_url",
    type: "customFile",
    name: "مرفق جواز السفر",
    when: (data) =>
      parseInt(data?.applicant_role_id) === 2 &&
      parseInt(data?.taxpayer?.nationality_id) !== 63,
  },
];

export const taxpayerSchema = [
  ...taxpayerCommonSchemaForRoles567,
  ...taxpayerSchemaForRole2,
];

// Group rule: corporate "at least one of"
// Applies only when applicant_role_id in [5,6,7] and taxpayer.type_id === 2
// export const taxpayerGroupRulesSchema = [
//   {
//     name: "corporateAtLeastOne",
//     type: "atLeastOne",
//     when: (data) =>
//       [5, 6, 7].includes(parseInt(data?.applicant_role_id)) &&
//       parseInt(data?.taxpayer?.type_id) === 2,
//     paths: [
//       "taxpayer.commercial_register",
//       "taxpayer.tax_card_number",
//       "taxpayer.other_attachment_name",
//       "taxpayer.commercial_register_attachment",
//       "taxpayer.tax_card_attachment",
//       "taxpayer.other_attachment",
//     ],
//     // Show errors on these "text" fields (mimics your existing UX)
//     errorKeys: [
//       "commercial_register",
//       "tax_card_number",
//       "other_attachment_name",
//     ],
//     message:
//       "يجب إدخال على الأقل حقل واحد من الحقول التالية: رقم السجل التجاري، الرقم الضريبي، أو أخرى",
//     onFail: (data, { setError, setValidFields }) => {
//       // If you need to surface a top-level banner/message:
//       if (typeof data?.__setUnitOwnerTypeErrorMessage === "function") {
//         data.__setUnitOwnerTypeErrorMessage(
//           "يجب إدخال على الأقل حقل واحد من الحقول التالية: رقم السجل التجاري، الرقم الضريبي، أو أخرى",
//         );
//       }
//     },
//   },
// ];

export const unitCommonAddressSchema = [
  {
    key: "property_type_id",
    path: "unit.property_type_id",
    type: "select",
    name: "نوع العقار",
  },
  {
    key: "reta_contact_about_unit",
    path: "unit.reta_contact_about_unit",
    type: "select",
    name: "إجابة",
    when: (data) => {
      const typeId = parseInt(data?.unit?.property_type_id);
      return typeId !== 10 && typeId !== 12;
    },
  },
  {
    key: "submit_other_supporting_documents",
    path: "unit.submit_other_supporting_documents",
    type: "select",
    name: "إجابة",
  },
  // {
  //   key: "known_build_num",
  //   path: "unit.known_build_num",
  //   type: "input",
  //   name: "رقم العقار/المبنى المتعارف عليه",
  //   when: (data) => {
  //     const number = data?.unit?.buildingProfile?.number;
  //     return !number || parseInt(number) === 0;
  //   },
  //   customValidate: (v) => {
  //     if (!v || String(v).trim() === "") {
  //       return {
  //         valid: false,
  //         message: "يجب إدخال رقم العقار/المبنى المتعارف عليه",
  //       };
  //     }
  //     return true;
  //   },
  // },
];

const residentialSchema = [
  {
    key: "real_estate_floor_id",
    path: "unit.real_estate_floor_id",
    type: "select",
    name: "الدور",
  },
  {
    key: "real_estate_floor_other",
    path: "unit.real_estate_floor_other",
    type: "input",
    name: "دور آخر",
    when: (data) => parseInt(data?.unit?.real_estate_floor_id) === -1,
  },
  // {
  //   key: "unit_id",
  //   path: "unit.unit_id",
  //   type: "select",
  //   name: "الوحدة",
  // },
  {
    key: "unit_number",
    path: "unit.unit_number",
    type: "input",
    name: "رقم الوحدة",
  },
  {
    key: "usage_type",
    path: "unit.usage_type",
    type: "input",
    name: "نوع الإستخدام",
  },
  {
    key: "area",
    path: "unit.area",
    type: "input",
    name: "المساحة",
  },
  {
    key: "unit_type_id",
    path: "unit.unit_type_id",
    type: "select",
    name: "نوع الوحدة",
  },
  // {
  //   key: "attachments",
  //   validKey: "attachment", // keep your original setValidFields('attachment')
  //   path: "unit.attachments",
  //   type: "array",
  //   name: "ملحقات بالوحدة",
  // },
];

const industrialAndProductionFacilitySchema = [
  {
    key: "facility_name",
    path: "unit.facility_name",
    type: "input",
    name: "اسم المنشأة",
  },
  {
    key: "activity_type",
    path: "unit.activity_type",
    type: "input",
    name: "نوع النشاط",
  },
  {
    key: "total_land_area",
    path: "unit.total_land_area",
    type: "input",
    name: "مساحة الأرض الكلية",
  },
  {
    key: "used_land_area",
    path: "unit.used_land_area",
    type: "input",
    name: "مساحة الأرض المستغلة",
  },
  {
    key: "buildings_count",
    path: "unit.buildings_count",
    type: "input",
    name: "عدد المباني",
  },
  {
    key: "burden_activity_id",
    path: "unit.burden_activity_id",
    type: "select",
    name: "النشاط المستفاد من التحمل",
    when: (data) => parseInt(data?.unit?.ministry_burden) === 1,
  },
];

const petroleumFacilitySchema = [
  {
    key: "facility_name",
    path: "unit.facility_name",
    type: "input",
    name: "اسم المنشأة",
  },
  {
    key: "usage_type",
    path: "unit.usage_type",
    type: "input",
    name: "نوع الإستخدام",
  },
  {
    key: "total_land_area",
    path: "unit.total_land_area",
    type: "input",
    name: "مساحة الأرض الكلية",
  },
  {
    key: "used_land_area",
    path: "unit.used_land_area",
    type: "input",
    name: "مساحة الأرض المستغلة",
  },
  {
    key: "buildings_count",
    path: "unit.buildings_count",
    type: "input",
    name: "عدد المباني",
  },
];

const commercialSchema = [
  {
    key: "real_estate_floor_id",
    path: "unit.real_estate_floor_id",
    type: "select",
    name: "الدور",
  },
  {
    key: "real_estate_floor_other",
    path: "unit.real_estate_floor_other",
    type: "input",
    name: "دور آخر",
    when: (data) => parseInt(data?.unit?.real_estate_floor_id) === -1,
  },
  // {
  //   key: "unit_id",
  //   path: "unit.unit_id",
  //   type: "select",
  //   name: "الوحدة",
  // },
  {
    key: "unit_number",
    path: "unit.unit_number",
    type: "input",
    name: "رقم الوحدة",
  },
  {
    key: "area",
    path: "unit.area",
    type: "input",
    name: "المساحة",
  },
  {
    key: "activity_type",
    path: "unit.activity_type",
    type: "input",
    name: "نوع النشاط",
  },
];

const administrativeSchema = [
  {
    key: "real_estate_floor_id",
    path: "unit.real_estate_floor_id",
    type: "select",
    name: "الدور",
  },
  {
    key: "real_estate_floor_other",
    path: "unit.real_estate_floor_other",
    type: "input",
    name: "دور آخر",
    when: (data) => parseInt(data?.unit?.real_estate_floor_id) === -1,
  },
  // {
  //   key: "unit_id",
  //   path: "unit.unit_id",
  //   type: "select",
  //   name: "الوحدة",
  // },
  {
    key: "unit_number",
    path: "unit.unit_number",
    type: "input",
    name: "رقم الوحدة",
  },
  {
    key: "area",
    path: "unit.area",
    type: "input",
    name: "المساحة",
  },
  {
    key: "activity_type",
    path: "unit.activity_type",
    type: "input",
    name: "نوع النشاط",
  },
];

const serviceUnitSchema = [
  {
    key: "real_estate_floor_id",
    path: "unit.real_estate_floor_id",
    type: "select",
    name: "الدور",
  },
  {
    key: "real_estate_floor_other",
    path: "unit.real_estate_floor_other",
    type: "input",
    name: "دور آخر",
    when: (data) => parseInt(data?.unit?.real_estate_floor_id) === -1,
  },
  // {
  //   key: "unit_id",
  //   path: "unit.unit_id",
  //   type: "select",
  //   name: "الوحدة",
  // },
  {
    key: "unit_number",
    path: "unit.unit_number",
    type: "input",
    name: "رقم الوحدة",
  },
  {
    key: "area",
    path: "unit.area",
    type: "input",
    name: "المساحة",
  },
  {
    key: "activity_type",
    path: "unit.activity_type",
    type: "input",
    name: "نوع النشاط",
  },
  {
    key: "exemption_reason",
    path: "unit.exemption_reason",
    type: "select",
    name: "سبب الإعفاء",
    when: (data) => parseInt(data?.unit?.exempted) === 1,
  },
  {
    key: "law_number",
    path: "unit.law_number",
    type: "input",
    name: "رقم القانون",
    when: (data) => parseInt(data?.unit?.exemption_reason) === 11,
  },
  {
    key: "law_year",
    path: "unit.law_year",
    type: "input",
    name: "سنة القانون",
    when: (data) => parseInt(data?.unit?.exemption_reason) === 11,
  },
];

const installationUnitSchema = [
  {
    key: "real_estate_floor_id",
    path: "unit.real_estate_floor_id",
    type: "select",
    name: "الدور",
  },
  {
    key: "real_estate_floor_other",
    path: "unit.real_estate_floor_other",
    type: "input",
    name: "دور آخر",
    when: (data) => parseInt(data?.unit?.real_estate_floor_id) === -1,
  },
  // {
  //   key: "unit_id",
  //   path: "unit.unit_id",
  //   type: "select",
  //   name: "الوحدة",
  // },
  // {
  //   key: "unit_number",
  //   path: "unit.unit_number",
  //   type: "input",
  //   name: "رقم الوحدة",
  // },
  {
    key: "installation_type_id",
    path: "unit.installation_type_id",
    type: "select",
    name: "نوع التركيب",
  },
  {
    key: "installation_type_other",
    path: "unit.installation_type_other",
    type: "input",
    name: "نوع التركيب الآخر",
    when: (data) => parseInt(data?.unit?.installation_type_id) === 3,
  },
  {
    key: "is_taxpayer_owner_of_installation",
    path: "unit.is_taxpayer_owner_of_installation",
    type: "select",
    name: "إجابة",
  },
  {
    key: "installation_owner_name",
    path: "unit.installation_owner_name",
    type: "input",
    name: "مالك التركيب",
  },
  {
    key: "contract_start",
    path: "unit.contract_start",
    type: "select",
    name: "تاريخ بداية التعاقد",
  },
  {
    key: "contract_end",
    path: "unit.contract_end",
    type: "select",
    name: "تاريخ نهاية التعاقد",
  },
];

const serviceFacilitySchema = [
  {
    key: "facility_name",
    path: "unit.facility_name",
    type: "input",
    name: "اسم المنشأة",
  },
  {
    key: "total_land_area",
    path: "unit.total_land_area",
    type: "input",
    name: "مساحة الأرض الكلية",
  },
  {
    key: "buildings_count",
    path: "unit.buildings_count",
    type: "input",
    name: "عدد المباني",
  },
  {
    key: "total_building_area",
    path: "unit.total_building_area",
    type: "input",
    name: "إجمالي مساحة المباني",
    required: true,
  },
  {
    key: "activity_type",
    path: "unit.activity_type",
    type: "input",
    name: "نوع النشاط",
  },
  {
    key: "exemption_reason",
    path: "unit.exemption_reason",
    type: "select",
    name: "سبب الإعفاء",
    when: (data) => parseInt(data?.unit?.exempted) === 1,
  },
  {
    key: "law_number",
    path: "unit.law_number",
    type: "input",
    name: "رقم القانون",
    when: (data) => parseInt(data?.unit?.exemption_reason) === 11,
  },
  {
    key: "law_year",
    path: "unit.law_year",
    type: "input",
    name: "سنة القانون",
    when: (data) => parseInt(data?.unit?.exemption_reason) === 11,
  },
];

const hotelFacilitiesSchema = [
  {
    key: "trade_name",
    path: "unit.trade_name",
    type: "input",
    name: "الإسم التجاري للمنشأة الفندقية",
  },
  {
    key: "view_type_id",
    path: "unit.view_type_id",
    type: "select",
    name: "الإطلالة",
  },
  {
    key: "license_date",
    path: "unit.license_date",
    type: "select",
    name: "تاريخ تراخيص التشغيل",
  },
  {
    key: "buildings_count",
    path: "unit.buildings_count",
    type: "input",
    name: "عدد المباني",
  },
  // {
  //   key: "rooms_count",
  //   path: "unit.rooms_count",
  //   type: "input",
  //   name: "عدد الغرف",
  // },
  {
    key: "star_rating_id",
    path: "unit.star_rating_id",
    type: "select",
    name: "مستوى النجومية",
  },
  {
    key: "reta_contact_about_unit",
    path: "unit.reta_contact_about_unit",
    type: "select",
    name: "إجابة",
  },
  {
    key: "has_sub_units",
    path: "unit.has_sub_units",
    type: "select",
    name: "إجابة",
  },
  {
    key: "hotelUnits",
    path: "unit.hotelUnits",
    type: "array",
    name: "وحدات المنشأة الفندقية",
    when: (data) => parseInt(data?.unit?.has_sub_units) === 1,
    customValidate: (units) => {
      if (!Array.isArray(units) || units.length === 0) {
        return { valid: false, message: "يجب إضافة وحدة واحدة على الأقل" };
      }
      return true;
    },
  },
];

const vacantLandSchema = [
  {
    key: "total_land_area",
    path: "unit.total_land_area",
    type: "input",
    name: "مساحة الأرض الكلية",
  },
  // {
  //   key: "used_land_area",
  //   path: "unit.used_land_area",
  //   type: "input",
  //   name: "المساحة المستغلة ",
  // },
  // {
  //   key: "exploitation_type_id",
  //   path: "unit.exploitation_type_id",
  //   type: "select",
  //   name: "نوع الإستغلال",
  // },
  // {
  //   key: "reta_contact_about_unit",
  //   path: "unit.reta_contact_about_unit",
  //   type: "select",
  //   name: "هل تم التواصل مع الضرائب العقارية بخصوص الوحدة محل الإقرار سابقا؟",
  // },
];

const MineQuarryFacilitySchema = [
  {
    key: "reta_contact_about_unit",
    path: "unit.reta_contact_about_unit",
    type: "select",
    name: "هل تم التواصل مع الضرائب العقارية بخصوص الوحدة محل الإقرار سابقا؟",
  },
  {
    key: "contract_end",
    path: "unit.contract_end",
    type: "select",
    name: "تاريخ نهاية التعاقد",
  },
  {
    key: "contract_start",
    path: "unit.contract_start",
    type: "select",
    name: "تاريخ بداية التعاقد",
  },
  {
    key: "licensed_area",
    path: "unit.licensed_area",
    type: "input",
    name: "المساحة المرخص بها",
  },
  {
    key: "extracted_material_id",
    path: "unit.extracted_material_id",
    type: "select",
    name: "نوع المادة المستخرجة",
  },
  {
    key: "facility_type_id",
    path: "unit.facility_type_id",
    type: "select",
    name: "نوع المنشأة",
  },
];
// Wrapper for property-type specific schema
export const getUnitCoreSchema = (data) => {
  const typeId = parseInt(data?.unit?.property_type_id);
  if (typeId === 1) return residentialSchema;
  if (typeId === 2) return commercialSchema;
  if (typeId === 3) return administrativeSchema;
  if (typeId === 4) return serviceUnitSchema;
  if (typeId === 5) return serviceFacilitySchema;
  if (typeId === 6) return hotelFacilitiesSchema;

  if (typeId === 7 || typeId === 8)
    return industrialAndProductionFacilitySchema;
  if (typeId === 9) return petroleumFacilitySchema;
  if (typeId === 10) return MineQuarryFacilitySchema;
  if (typeId === 11) return installationUnitSchema;
  if (typeId === 12) return vacantLandSchema;
  return null; // or [] if not required for other types
};
